import React from 'react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MessageSquare, Sparkles, TrendingUp, MapPin, Phone } from "lucide-react";
import { Link } from "react-router-dom";
import MemberFeedbackDialog from "./MemberFeedbackDialog";

interface WelcomeAuctionBannerProps {
  className?: string;
}

const WelcomeAuctionBanner: React.FC<WelcomeAuctionBannerProps> = ({ 
  className 
}) => {
  return (
    <div className={`relative overflow-hidden bg-gradient-to-r from-emerald-950/95 via-teal-950/90 to-blue-950/95 backdrop-blur-xl border-b border-white/10 shadow-xl ${className}`}>
      {/* Elegant background effects */}
      <div className="absolute inset-0">
        <div className="absolute -left-24 -top-12 w-96 h-48 bg-gradient-to-br from-emerald-500/10 to-teal-500/5 rounded-full blur-3xl animate-pulse" />
        <div className="absolute -right-24 -bottom-12 w-96 h-48 bg-gradient-to-br from-blue-500/10 to-teal-500/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      </div>
      
      {/* Decorative gradient line at bottom - matching navbar */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-emerald-500/30 to-transparent" />
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6 relative z-10">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
          {/* Left content - Feedback Call to Action */}
          <div className="flex flex-col lg:flex-row items-center gap-4 flex-1">
            <div className="flex items-center gap-4">
              <div className="relative">
                <div className="p-3 bg-gradient-to-br from-emerald-500/20 via-teal-500/20 to-blue-500/20 backdrop-blur-sm rounded-xl border border-white/20 shadow-lg">
                  <MessageSquare className="h-6 w-6 text-emerald-400" />
                </div>
                <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-emerald-500/20 to-blue-500/20 blur-xl opacity-50" />
              </div>
              <div className="space-y-1.5 text-center lg:text-left">
                <div className="flex items-center justify-center lg:justify-start gap-2">
                  <Badge className="bg-gradient-to-r from-emerald-500/90 to-teal-500/90 text-white text-xs px-3 py-1 rounded-xl border border-white/20 font-light tracking-wider uppercase">
                    <Sparkles className="h-3 w-3 mr-1 inline" />
                    We Value Your Input
                  </Badge>
                </div>
                <h2 className="text-xl font-medium text-white tracking-wide bg-gradient-to-r from-white to-emerald-100 bg-clip-text">
                  Help Us Improve Your Experience
                </h2>
                <p className="text-sm text-white/70 font-light max-w-md">
                  Share your feedback, report issues, or suggest improvements. Your voice shapes our platform!
                </p>
              </div>
            </div>
            
            {/* Contact Info - Hidden on mobile, shown on larger screens */}
            <div className="hidden lg:flex items-center gap-6 ml-6 text-sm text-white/70">
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-emerald-400" />
                <span className="font-light">8214 Griffin Rd, Davie, FL 33328</span>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-teal-400" />
                <a href="tel:+19546050748" className="font-light hover:text-emerald-300 transition-colors">
                  +1 (954) 605-0748
                </a>
              </div>
            </div>
          </div>
          
          {/* Right side - Action buttons */}
          <div className="flex flex-col sm:flex-row items-center gap-3">
            <MemberFeedbackDialog 
              trigger={
                <Button 
                  size="lg"
                  className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white font-light tracking-wide rounded-xl border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                >
                  <MessageSquare className="h-5 w-5 mr-2" />
                  Share Feedback
                </Button>
              }
            />
            <Button 
              asChild
              size="lg"
              variant="outline"
              className="border border-white/20 text-white/80 hover:text-emerald-300 hover:bg-white/10 font-light tracking-wide rounded-xl transition-all duration-300 hover:scale-105 bg-transparent backdrop-blur-sm hover:border-white/30"
            >
              <Link to="/auctions">
                <TrendingUp className="h-5 w-5 mr-2" />
                View Auctions
              </Link>
            </Button>
          </div>
        </div>
        
        {/* Mobile contact info - Shown only on mobile */}
        <div className="lg:hidden flex flex-col gap-2 mt-4 pt-4 border-t border-white/10">
          <div className="flex items-center justify-center gap-2 text-sm text-white/70">
            <MapPin className="h-4 w-4 text-emerald-400" />
            <span className="font-light">8214 Griffin Rd, Davie, FL 33328</span>
          </div>
          <div className="flex items-center justify-center gap-2 text-sm text-white/70">
            <Phone className="h-4 w-4 text-teal-400" />
            <a href="tel:+19546050748" className="font-light hover:text-emerald-300 transition-colors">
              +1 (954) 605-0748
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WelcomeAuctionBanner;